import { useEffect, useState } from "react";
import api from "../lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Package, Wrench, Copy, ExternalLink, CheckCircle2, X, Store } from "lucide-react";

const MODALITIES = ["por_visita", "contrato_mensual", "por_evento", "cotizacion_individual"];
const RESPONSE_TIMES = ["24h", "48h", "72h", "semanal", "mensual"];

export const AddSupplierModal = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [branches, setBranches] = useState([]);
  const [success, setSuccess] = useState(null); // { supplier_id, completion_url, business_name, status }
  const [branchOpsMode, setBranchOpsMode] = useState(null); // null | "same" | "different" | "later"

  const [form, setForm] = useState({
    business_name: "",
    supplier_type: "insumos",
    category_hint: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    rfc: "",
    credit_days: 0,
    branches_authorized: [],
    service_modality: "",
    service_response_time: "",
    service_description: "",
  });

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleBranch = (id) =>
    setForm((f) => ({
      ...f,
      branches_authorized: f.branches_authorized.includes(id)
        ? f.branches_authorized.filter((b) => b !== id)
        : [...f.branches_authorized, id],
    }));

  const canNext = form.business_name.trim().length >= 2 && !!form.supplier_type;
  const multiBranch = form.branches_authorized.length >= 2;
  const defaultBranchOpsMode = form.supplier_type === "servicios" ? "different" : null;
  const effectiveBranchOpsMode = branchOpsMode || defaultBranchOpsMode;

  const submit = async () => {
    if (form.contact_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contact_email.trim())) {
      toast.error(t("addSupplier.invalidEmail"));
      return;
    }
    setBusy(true);
    try {
      const payload = {
        business_name: form.business_name.trim(),
        supplier_type: form.supplier_type,
        credit_days: Number(form.credit_days) || 0,
        branches_authorized: form.branches_authorized,
      };
      if (form.category_hint.trim()) payload.category_hint = form.category_hint.trim();
      if (form.contact_name.trim()) payload.contact_name = form.contact_name.trim();
      if (form.contact_email.trim()) payload.contact_email = form.contact_email.trim().toLowerCase();
      if (form.contact_phone.trim()) payload.contact_phone = form.contact_phone.trim();
      if (form.rfc.trim()) payload.rfc = form.rfc.trim().toUpperCase();
      if (form.supplier_type === "servicios") {
        if (form.service_modality) payload.service_modality = form.service_modality;
        if (form.service_response_time) payload.service_response_time = form.service_response_time;
        if (form.service_description.trim()) payload.service_description = form.service_description.trim();
      }
      const r = await api.post("/suppliers", payload);
      const supplierId = r.data.supplier_id;
      onCreated?.();
      if (multiBranch && effectiveBranchOpsMode === "same") {
        try {
          await api.post(`/suppliers/${supplierId}/branch-operations/bootstrap`, {
            mode: "same_config_all",
            shared_config: { vendor_name: form.contact_name.trim() || null, orders_email: form.contact_email.trim() || null },
          });
          toast.success(t("supplierEdit.branchOps.bootstrapDone"));
        } catch (e) {
          toast.error(e?.response?.data?.detail || t("common.error"));
        }
        navigate(`/proveedores/${supplierId}`);
        return;
      }
      if (multiBranch && effectiveBranchOpsMode === "different") {
        navigate(`/proveedores/${supplierId}?configure_branch_ops=1`);
        return;
      }
      setSuccess({ ...r.data, business_name: form.business_name.trim() });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("addSupplier.createError"));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(success.completion_url);
    toast.success(t("addSupplier.linkCopied"));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        data-testid="add-supplier-modal"
        className="bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>

        {success ? (
          <div data-testid="add-supplier-success">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-600 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-base text-[#1A1A1A]">{t("addSupplier.successTitle", { name: success.business_name })}</div>
                <div className="text-xs text-slate-500">{t(`addSupplier.statusLabel.${success.status}`)}</div>
              </div>
            </div>
            <p className="text-xs text-slate-600 mb-3 leading-relaxed">{t("addSupplier.successHint")}</p>
            <div className="flex items-center gap-2 mb-4">
              <div data-testid="add-supplier-completion-url" className="flex-1 text-xs font-mono bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 truncate">
                {success.completion_url}
              </div>
              <button data-testid="add-supplier-copy-link-btn" onClick={copyLink}
                className="w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0">
                <Copy className="w-4 h-4 text-slate-600" />
              </button>
            </div>
            <div className="flex gap-2">
              <button data-testid="add-supplier-goto-detail-btn" onClick={() => navigate(`/proveedores/${success.supplier_id}`)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 bg-[#1A1A1A] text-white rounded-xl text-sm font-bold hover:bg-black">
                <ExternalLink className="w-4 h-4" /> {t("addSupplier.gotoDetail")}
              </button>
              <button data-testid="add-supplier-success-close-btn" onClick={onClose}
                className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">
                {t("common.close")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-base text-[#1A1A1A]">{t("addSupplier.title")}</div>
              <button data-testid="add-supplier-close-btn" onClick={onClose} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center gap-2 mb-5 text-xs font-semibold">
              <span className={step === 1 ? "text-[#1A1A1A]" : "text-slate-400"}>1. {t("addSupplier.step1")}</span>
              <span className="text-slate-300">›</span>
              <span className={step === 2 ? "text-[#1A1A1A]" : "text-slate-400"}>2. {t("addSupplier.step2")}</span>
            </div>

            {step === 1 ? (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("addSupplier.businessName")} *</label>
                  <input data-testid="add-supplier-name-input" type="text" value={form.business_name}
                    onChange={(e) => set("business_name", e.target.value)}
                    placeholder={t("addSupplier.businessNamePh")}
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("addSupplier.type")} *</label>
                  <div className="grid grid-cols-2 gap-2.5">
                    {[
                      { v: "insumos", icon: Package, l: t("suppliers.typeInsumos") },
                      { v: "servicios", icon: Wrench, l: t("suppliers.typeServicios") },
                    ].map((o) => (
                      <button key={o.v} data-testid={`add-supplier-type-${o.v}`} onClick={() => set("supplier_type", o.v)}
                        className={`flex items-center gap-2 px-3 py-3 rounded-xl border-2 text-sm font-semibold transition ${
                          form.supplier_type === o.v ? "border-[#FFDF00] bg-[#FFFBE6]" : "border-slate-200 hover:bg-slate-50"
                        }`}>
                        <o.icon className="w-4 h-4" /> {o.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("addSupplier.category")}</label>
                  <input data-testid="add-supplier-category-input" type="text" value={form.category_hint}
                    onChange={(e) => set("category_hint", e.target.value)}
                    placeholder={form.supplier_type === "servicios" ? t("addSupplier.categoryServiciosPh") : t("addSupplier.categoryInsumosPh")}
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                </div>
                <button data-testid="add-supplier-next-btn" onClick={() => setStep(2)} disabled={!canNext}
                  className="btn-primary-chizy w-full py-2.5 rounded-xl text-sm disabled:opacity-50">
                  {t("common.next")}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("addSupplier.contactName")}</label>
                    <input data-testid="add-supplier-contact-name-input" type="text" value={form.contact_name}
                      onChange={(e) => set("contact_name", e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("addSupplier.contactPhone")}</label>
                    <input data-testid="add-supplier-contact-phone-input" type="text" value={form.contact_phone}
                      onChange={(e) => set("contact_phone", e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("addSupplier.contactEmail")}</label>
                  <input data-testid="add-supplier-contact-email-input" type="email" value={form.contact_email}
                    onChange={(e) => set("contact_email", e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("addSupplier.rfc")}</label>
                    <input data-testid="add-supplier-rfc-input" type="text" value={form.rfc}
                      onChange={(e) => set("rfc", e.target.value.toUpperCase())}
                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("addSupplier.creditDays")}</label>
                    <input data-testid="add-supplier-credit-days-input" type="number" min="0" value={form.credit_days}
                      onChange={(e) => set("credit_days", e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("addSupplier.branches")}</label>
                  <div className="flex flex-wrap gap-2">
                    {branches.map((b) => (
                      <button key={b.id} data-testid={`add-supplier-branch-${b.code || b.id}`} onClick={() => toggleBranch(b.id)}
                        className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition ${
                          form.branches_authorized.includes(b.id) ? "border-[#FFDF00] bg-[#FFFBE6] text-[#1A1A1A]" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                        }`}>
                        {b.name}
                      </button>
                    ))}
                  </div>
                </div>
                {multiBranch && (
                  <div data-testid="add-supplier-branch-ops-question" className="space-y-2.5 border-t border-slate-100 pt-3.5">
                    <label className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
                      <Store className="w-3.5 h-3.5" /> {t("supplierEdit.branchOps.bootstrapQuestion")}
                    </label>
                    <div className="space-y-1.5">
                      {[
                        { v: "different", l: t("supplierEdit.branchOps.bootstrapDifferent") },
                        { v: "same", l: t("supplierEdit.branchOps.bootstrapSame") },
                        { v: "later", l: t("supplierEdit.branchOps.bootstrapLater") },
                      ].map((o) => (
                        <label key={o.v} data-testid={`add-supplier-branch-ops-${o.v}`}
                          className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer text-xs ${
                            effectiveBranchOpsMode === o.v ? "border-[#FFDF00] bg-[#FFFBE6] font-semibold" : "border-slate-200 hover:bg-slate-50"
                          }`}>
                          <input type="radio" name="branch_ops_mode" value={o.v} checked={effectiveBranchOpsMode === o.v}
                            onChange={() => setBranchOpsMode(o.v)} className="w-3.5 h-3.5 text-[#FFDF00] focus:ring-[#FFDF00]" />
                          {o.l}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {form.supplier_type === "servicios" && (
                  <div className="space-y-3 border-t border-slate-100 pt-3" data-testid="add-supplier-service-fields">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("supplierReg.serviceModality")}</label>
                        <select data-testid="add-supplier-modality-select" value={form.service_modality}
                          onChange={(e) => set("service_modality", e.target.value)}
                          className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                          <option value="">—</option>
                          {MODALITIES.map((m) => <option key={m} value={m}>{t(`supplierReg.modalityOptions.${m}`)}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("supplierReg.serviceResponseTime")}</label>
                        <select data-testid="add-supplier-response-select" value={form.service_response_time}
                          onChange={(e) => set("service_response_time", e.target.value)}
                          className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                          <option value="">—</option>
                          {RESPONSE_TIMES.map((r) => <option key={r} value={r}>{t(`supplierReg.responseTimeOptions.${r}`)}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("addSupplier.serviceDescription")}</label>
                      <textarea data-testid="add-supplier-service-desc-input" rows={2} value={form.service_description}
                        onChange={(e) => set("service_description", e.target.value)}
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <button data-testid="add-supplier-back-btn" onClick={() => setStep(1)} disabled={busy}
                    className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">
                    {t("common.back")}
                  </button>
                  <button data-testid="add-supplier-submit-btn" onClick={submit} disabled={busy}
                    className="btn-primary-chizy flex-1 py-2.5 rounded-xl text-sm disabled:opacity-50">
                    {busy ? t("common.saving") : t("addSupplier.create")}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
};
